import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import { ErrorText } from '@mummssoftware/common-ui/style';
import { handleTimeChange } from '@mummssoftware/utils/visitUtils';
import {
  getMinDuration,
  getHcpcsDurationPersistPayload,
} from '@mummssoftware/utils/hcpcsValidationUtils';
import { HCPCSContext } from '../Context/contexts/HCPCSContext';
import {
  SET_HCPCS_DURATION,
  SET_HCPCS_ERROR,
  RECORD_DATA,
} from '../Context/constants';
import { TimerDiv, EditTimeDiv } from '../../components';
import { useStyles } from '../Timer/style';
import defaults from '../../translations/en.json';

type HCPCSDurationProps = {
  hcpcsChoice: mumms.HCPCSMenuItem | null;
};

export const HCPCSDuration = ({ hcpcsChoice }: HCPCSDurationProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const { dispatchHCPCS, hcpcsDuration } = useContext(HCPCSContext);

  const initDuration =
    hcpcsDuration && hcpcsChoice && hcpcsDuration[hcpcsChoice.key];

  const [duration, setDuration] = useState<number>(initDuration || 0);

  const minMinute = getMinDuration(hcpcsChoice);
  const maxMinute =
    hcpcsChoice && hcpcsChoice.maxTime ? hcpcsChoice.maxTime : null;

  const minTimeError = duration > 0 && duration < minMinute;
  const maxTimeError = duration > 0 && maxMinute && duration > maxMinute;

  useEffect(() => {
    if (initDuration) {
      setDuration(initDuration);
    }
  }, [initDuration]);

  const persistDuration = useCallback(
    (rawValue: number | string) => {
      const payload = getHcpcsDurationPersistPayload(hcpcsChoice, rawValue);
      if (!payload) {
        return;
      }

      dispatchHCPCS({
        type: SET_HCPCS_DURATION,
        payload,
      });
    },
    [dispatchHCPCS, hcpcsChoice],
  );

  useEffect(() => {
    if (Number(duration) === 0) {
      dispatchHCPCS({
        type: SET_HCPCS_ERROR,
        payload: true,
      });
    } else if (!minTimeError && !maxTimeError) {
      dispatchHCPCS({
        type: SET_HCPCS_ERROR,
        payload: false,
      });
    } else {
      dispatchHCPCS({
        type: SET_HCPCS_ERROR,
        payload: true,
      });
    }
  }, [duration, minTimeError, maxTimeError, dispatchHCPCS]);

  const onMinChange = (event: any) => {
    const targetValue = event.target.value;
    setDuration(targetValue);
    dispatchHCPCS({
      type: RECORD_DATA,
      payload: true,
    });
    persistDuration(targetValue);
  };

  const onKeyDown = (e: any) => {
    if (e.keyCode === 13) {
      persistDuration(e.target.value);
    }
  };
  const handleMinChange = handleTimeChange(onMinChange);

  const onMinSave = (event: any) => {
    persistDuration(event.target.value);
  };

  const inputProps = {
    classes: {
      input: classes.input,
      root: minTimeError ? classes.outlinedError : classes.outlined,
    },
  };

  return (
    <EditTimeDiv className="edit-hcpcs-time-div">
      <FormHelperText id="component-helper-text">
        {duration ? defaults.duration : defaults.enterDuration}
      </FormHelperText>

      <TimerDiv edit>
        <TextField
          label="MM"
          id="min-edit"
          variant="outlined"
          value={duration}
          InputProps={inputProps}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputProps={{ maxLength: 4 }}
          onChange={handleMinChange}
          onKeyDown={onKeyDown}
          onBlur={onMinSave}
          InputLabelProps={{
            shrink: true,
            classes: { root: classes.labelInput },
          }}
        />
      </TimerDiv>

      {minTimeError && (
        <ErrorText>
          {intl.formatMessage(defaults.errorHCPCS, {
            minMinutes: minMinute,
          })}
        </ErrorText>
      )}
      {maxTimeError && (
        <ErrorText>
          {intl.formatMessage(defaults.errorMaxHCPCS, {
            maxMinutes: maxMinute,
          })}
        </ErrorText>
      )}
    </EditTimeDiv>
  );
};

export default HCPCSDuration;
